import { useCallback, useState } from 'react';
import {
    Button,
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Dialog,
} from '@origo/origo-ui';
import {
    PencilSimpleIcon,
    PlusIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import type { Område, OmrådeParentType } from '@origo/seksjon';
import { useOmråder } from '../../planer/hooks/useOmråder';
import styles from './EgendefinerteOmraderAdmin.module.css';

interface Props {
    egendefinerte: Område[];
    /** Klikk på et område-card → admin-shell setter 'omrade'-kontekst. */
    onSelectOmråde: (item: Område) => void;
    /** Åpne OmrådeEditorPanel for å opprette et nytt egendefinert område. */
    onAdd: () => void;
    /** Åpne OmrådeEditorPanel for å redigere et eksisterende område. */
    onEditOmråde: (item: Område) => void;
    /** Valgfri: hent delområde-tre for parent-label-resolusjon. */
    delområder?: Område[];
    /** PlanId for delete-mutasjon. */
    planId?: string;
}

function parentLabel(item: Område, egendefinerte: readonly Område[], delområder: readonly Område[]): string {
    if (!item.parentType || !item.parentId) return 'Rotnivå';
    switch (item.parentType as OmrådeParentType) {
        case 'Plan':
            return 'Underområde av planen';
        case 'Delområdetype': {
            const parent = delområder.find((d) => d.id === item.parentId);
            return parent ? `Under ${parent.navn}` : 'Under delområdetype';
        }
        case 'Delområde': {
            for (const group of delområder) {
                const child = group.children?.find((c) => c.id === item.parentId);
                if (child) return `Under ${child.navn}`;
            }
            return 'Under delområde';
        }
        case 'Område': {
            const parent = egendefinerte.find((o) => o.id === item.parentId);
            return parent ? `Under ${parent.navn}` : 'Under egendefinert område';
        }
        default:
            return 'Rotnivå';
    }
}

export function EgendefinerteOmraderAdmin({
    egendefinerte,
    onSelectOmråde,
    onAdd,
    onEditOmråde,
    delområder = [],
    planId,
}: Readonly<Props>) {
    const { deleteOmråde } = useOmråder(planId ?? '');
    const [deleteTarget, setDeleteTarget] = useState<Område | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleDelete = useCallback((item: Område) => {
        setDeleteTarget(item);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget || deleteTarget.objectId == null) return;
        setDeleting(true);
        try {
            await deleteOmråde(deleteTarget.objectId, Number(deleteTarget.id));
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    }, [deleteTarget, deleteOmråde]);

    const hasOmråder = egendefinerte.length > 0;

    return (
        <>
        <div className={styles.root}>
            {hasOmråder ? (
                <>
                    <header className={styles.header}>
                        <div>
                            <h2 className={styles.title}>Egendefinerte områder</h2>
                            <p className={styles.subtitle}>
                                Egendefinerte områder lar deg lage veiledning for spesifikke steder
                                som ikke finnes i kartet fra før. Dette kan for eksempel være en sti,
                                en lekeplass eller et utvalg eiendommer.
                            </p>
                        </div>
                        <Button variant="primary" leftIcon={<PlusIcon size={16} />} onClick={onAdd}>
                            Nytt område
                        </Button>
                    </header>

                    <div className={styles.list}>
                        {egendefinerte.map((item) => (
                            <Card key={item.id}>
                                <CardHeader>
                                    <div className={styles.cardHeaderRow}>
                                        <button
                                            type="button"
                                            className={styles.cardTitle}
                                            onClick={() => onSelectOmråde(item)}
                                        >
                                            {item.navn}
                                        </button>
                                        <div className={styles.cardActions}>
                                            <IconButton
                                                icon={<PencilSimpleIcon size={16} />}
                                                label="Rediger område"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onEditOmråde(item)}
                                            />
                                            <IconButton
                                                icon={<TrashIcon size={16} />}
                                                label="Slett område"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(item)}
                                            />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className={styles.parentInfo}>
                                        {parentLabel(item, egendefinerte, delområder)}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            ) : (
                <div className={styles.empty}>
                    <h2 className={styles.emptyTitle}>Egendefinerte områder</h2>
                    <p className={styles.emptyBody}>
                        Egendefinerte områder lar deg lage veiledning for spesifikke steder som ikke
                        finnes i kartet fra før. Dette kan for eksempel være en sti, en lekeplass
                        eller et utvalg eiendommer.
                    </p>
                    <Button variant="primary" onClick={onAdd}>
                        Nytt område
                    </Button>
                </div>
            )}
        </div>

        <Dialog
            open={deleteTarget !== null}
            onClose={() => setDeleteTarget(null)}
            title="Slett område"
            footer={
                <div className={styles.dialogFooter}>
                    <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
                        Slett område
                    </Button>
                </div>
            }
        >
            <p>Er du sikker på at du vil slette «{deleteTarget?.navn}»?</p>
        </Dialog>
        </>
    );
}
